import { CircleAlert, CircleCheck, Search } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  type BankDayDto,
  FIXTURE_RANGE,
  getBankDays,
  getLedgerEntries,
  type LedgerEntryDto,
} from '@/api/reconciliation';
import { BankDayGroup } from '@/components/reconciliation/bank-day-group';
import { LedgerEntryRow } from '@/components/reconciliation/ledger-entry-row';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { formatExactAmount, formatNetAmount } from '@/lib/format-exact-amount';
import { maskAccountNumber } from '@/lib/mask-account-number';
import { formatCalendarDate } from '@/lib/utils';

/**
 * Reconciliation.
 *
 * The layout is ported from the Lovable prototype. It renders from static fixtures
 * and makes no API call: the ledger read API and the bank read API arrive in the
 * side-by-side story, which swaps the fixture module for live calls and should not
 * need to restyle anything here.
 *
 * No matching interaction exists yet. The selection summary is rendered and inert,
 * and the row checkboxes are disabled, so the matching story has the geometry it
 * needs without this story implying behavior it does not have.
 */
export const Reconciliation: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(FIXTURE_RANGE.from);
  const [dateTo, setDateTo] = useState(FIXTURE_RANGE.to);
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const [hideMatched, setHideMatched] = useState(false);

  const [bankDays, setBankDays] = useState<BankDayDto[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntryDto[]>([]);

  useEffect(() => {
    let active = true;
    const range = { from: dateFrom, to: dateTo };
    Promise.all([getBankDays(range), getLedgerEntries(range)]).then(([bank, ledger]) => {
      if (!active) return;
      setBankDays(bank.data);
      setLedgerEntries(ledger.data);
    });
    return () => {
      active = false;
    };
  }, [dateFrom, dateTo]);

  // Counted from what was fetched for the range, not from what is currently
  // visible. A counter that fell while the operator typed in a search box would
  // describe the filter rather than the range.
  const matchTally = useMemo(() => {
    const rows = [
      ...ledgerEntries.map((e) => e.matched),
      ...bankDays.flatMap((d) => d.transactions.map((t) => t.matched)),
    ];
    const matched = rows.filter(Boolean).length;
    return { matched, total: rows.length, unmatched: rows.length - matched };
  }, [ledgerEntries, bankDays]);

  // The accounts the range covers, in the order their days arrive. More than one
  // is the normal case, and it decides two things: whether a day row has to name
  // its account, and how many balance strips there are.
  const bankAccounts = useMemo(
    () => [...new Set(bankDays.map((d) => d.accountNumber))],
    [bankDays]
  );

  // The design shows a Beginning and an Ending figure above the day list. Both are
  // picked, not computed: the opening ledger balance of the first day in range that
  // reports one, and the closing ledger balance of the last. No addition, so no
  // float risk. The design's third figure, the Difference between them, needs
  // subtraction and waits for the matching story.
  //
  // Picked within one account, never across them. A balance belongs to the account
  // that reported it, so a single pair taken over a mixed list would open on one
  // account and close on another and read as that account's movement for the
  // range. One strip per account instead, in the same order as the day list.
  const bankBalanceStrips = useMemo(
    () =>
      bankAccounts.map((accountNumber) => {
        const days = bankDays.filter((d) => d.accountNumber === accountNumber);
        const opening = days.find((d) =>
          d.balances.some((b) => b.typeCode === '010' && b.amount !== null)
        );
        const closing = [...days]
          .reverse()
          .find((d) => d.balances.some((b) => b.typeCode === '015' && b.amount !== null));
        return {
          accountNumber,
          beginning: opening?.balances.find((b) => b.typeCode === '010')?.amount ?? null,
          ending: closing?.balances.find((b) => b.typeCode === '015')?.amount ?? null,
        };
      }),
    [bankDays, bankAccounts]
  );

  const visibleLedger = useMemo(() => {
    const needle = ledgerSearch.trim().toLowerCase();
    return ledgerEntries.filter((entry) => {
      if (hideMatched && entry.matched) return false;
      if (!needle) return true;
      return [
        entry.clientName,
        entry.clientNumber,
        entry.paymentId,
        entry.externalReferenceId,
        entry.category,
        entry.paymentFileReference,
      ].some((field) => field.toLowerCase().includes(needle));
    });
  }, [ledgerEntries, ledgerSearch, hideMatched]);

  // Every day in the range is a row, and no filter removes one. A search narrows
  // the movements drawn inside a day; it never decides whether the day is on the
  // page. Dropping a day would take its delivery state, its entry count and its
  // reported balances off the screen with it, and none of those are things the
  // operator asked to filter: 29 Jun and 6 Jul both carry real opening and closing
  // figures that a search over movement text has no business hiding.
  //
  // Nothing here rewrites `day.transactions` either. The day header counts from
  // that list, and a count that moved while the operator typed would report the
  // search rather than the day. `BankDayGroup` is handed the whole day plus both
  // filters and decides what to draw.

  // What emptied a pane decides what it says. "for this range" is only true when
  // the range itself returned nothing; a filter that removed everything has to
  // name itself instead of blaming the range. Both branch on the fetched list,
  // never on the visible one.
  const ledgerEmptyMessage = (): string => {
    if (ledgerEntries.length === 0) return 'No ledger rows for this range.';
    if (ledgerSearch.trim()) return 'No ledger rows match your search.';
    return 'Every ledger row in this range is matched and hidden.';
  };

  // The bank pane needs no such branch. No filter removes a day, so the pane is
  // empty only when the range itself came back empty, and the copy is true by
  // construction rather than by checking.
  const bankEmptyMessage = (): string => 'No bank activity for this range.';

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="flex flex-wrap items-start justify-between gap-4 px-4 lg:px-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Reconciliation</h1>
          <p className="text-sm text-muted-foreground">
            {/* The range, and nothing about scope. This said "All accounts", which
                the response cannot back: a day entry is one account's statement for
                one date, and whether a single call may span accounts is #490's and
                #492's open contract question. The account each day belongs to is
                named on the day itself, where it is true. */}
            {formatCalendarDate(dateFrom)} to {formatCalendarDate(dateTo)}
          </p>
        </div>
        {/* Counts, not money. Summing amounts needs exact decimal addition, which
            belongs to the matching story, and two days in the range have contents
            nobody can know, so a money total for the range would be a guess. */}
        <dl className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1">
          <div className="flex items-center gap-1.5">
            <CircleCheck className="h-4 w-4 text-matched" aria-hidden="true" />
            <dt className="sr-only">Matched</dt>
            <dd className="m-0 text-sm tabular-nums text-muted-foreground">
              {matchTally.matched} of {matchTally.total} matched
            </dd>
          </div>
          <div className="flex items-center gap-1.5">
            <CircleAlert className="h-4 w-4 text-delta-unbalanced" aria-hidden="true" />
            <dt className="sr-only">Unmatched</dt>
            <dd className="m-0 text-sm tabular-nums text-muted-foreground">
              {matchTally.unmatched} unmatched
            </dd>
          </div>
        </dl>
      </div>

      {/* Controls. The range is deliberately not defaulted to a calendar month:
          the operator's working set spans a month boundary, because a deposit
          landing on the 1st is explained by ledger rows dated in the month before. */}
      <div className="flex flex-wrap items-end gap-4 px-4 lg:px-6">
        <div className="space-y-2">
          <Label htmlFor="reconciliation-date-from">From date</Label>
          <Input
            id="reconciliation-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-auto"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reconciliation-date-to">To date</Label>
          <Input
            id="reconciliation-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-auto"
          />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch
            id="reconciliation-hide-matched"
            checked={hideMatched}
            onCheckedChange={setHideMatched}
          />
          <Label htmlFor="reconciliation-hide-matched">Hide matched</Label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 px-4 lg:grid-cols-2 lg:gap-0 lg:px-6">
        {/* Ledger pane */}
        <section
          aria-labelledby="reconciliation-ledger-heading"
          className="min-w-0 border border-border lg:border-r-0"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3">
            <h2
              id="reconciliation-ledger-heading"
              className="font-medium text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Ledger
            </h2>
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={ledgerSearch}
                onChange={(e) => setLedgerSearch(e.target.value)}
                placeholder="Search ledger"
                aria-label="Search ledger"
                className="h-8 w-56 pl-8"
              />
            </div>
          </div>
          {visibleLedger.length > 0 ? (
            visibleLedger.map((entry) => <LedgerEntryRow key={entry.id} entry={entry} />)
          ) : (
            <p className="m-0 px-4 py-6 text-center text-sm text-muted-foreground">
              {ledgerEmptyMessage()}
            </p>
          )}
        </section>

        {/* Bank pane */}
        <section
          aria-labelledby="reconciliation-bank-heading"
          className="min-w-0 border border-border"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3">
            <h2
              id="reconciliation-bank-heading"
              className="font-medium text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Bank
            </h2>
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                placeholder="Search bank activity"
                aria-label="Search bank activity"
                className="h-8 w-56 pl-8"
              />
            </div>
          </div>
          {/* Absent stays absent. One account never reports a balance in any file,
              so a dash is the honest rendering and 0.00 would claim a figure the
              feed does not carry. One strip per account: these figures are that
              account's, and a range with two accounts has two of them. */}
          {bankBalanceStrips.map((strip) => (
            <dl
              key={strip.accountNumber}
              className="flex flex-wrap gap-x-6 gap-y-1 border-b border-border bg-background px-4 py-2 font-mono text-[11px]"
            >
              {bankAccounts.length > 1 && (
                <div className="flex gap-1.5">
                  <dt className="sr-only">Account</dt>
                  <dd className="m-0 font-semibold text-foreground">
                    {maskAccountNumber(strip.accountNumber)}
                  </dd>
                </div>
              )}
              <div className="flex gap-1.5">
                <dt className="text-muted-foreground">Beginning</dt>
                <dd className="m-0 tabular-nums text-foreground">
                  {strip.beginning === null ? (
                    <>
                      <span aria-hidden="true">&ndash;</span>
                      <span className="sr-only">Not reported</span>
                    </>
                  ) : (
                    formatNetAmount(strip.beginning)
                  )}
                </dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-muted-foreground">Ending</dt>
                <dd className="m-0 tabular-nums text-foreground">
                  {strip.ending === null ? (
                    <>
                      <span aria-hidden="true">&ndash;</span>
                      <span className="sr-only">Not reported</span>
                    </>
                  ) : (
                    formatNetAmount(strip.ending)
                  )}
                </dd>
              </div>
            </dl>
          ))}
          {bankDays.length > 0 ? (
            bankDays.map((day) => (
              // Keyed by account and date together. A date is not unique: two
              // accounts report on the same day, and a date-only key collapses
              // them onto one another.
              <BankDayGroup
                key={`${day.accountNumber}-${day.date}`}
                day={day}
                showAccount={bankAccounts.length > 1}
                hideMatched={hideMatched}
                search={bankSearch}
              />
            ))
          ) : (
            <p className="m-0 px-4 py-6 text-center text-sm text-muted-foreground">
              {bankEmptyMessage()}
            </p>
          )}
        </section>
      </div>

      {/* Selection summary. Inert: the per-side totals, the difference and the
          commit action all belong to the matching story. Nothing is selectable
          yet, so every figure here reads zero by construction. */}
      <div className="px-4 lg:px-6">
        <section
          aria-labelledby="reconciliation-summary-heading"
          className="flex flex-wrap items-center gap-x-8 gap-y-3 border border-border bg-card px-4 py-3"
        >
          <h2
            id="reconciliation-summary-heading"
            className="font-medium text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Selection
          </h2>
          <dl className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <div>
              <dt className="font-medium text-[10px] uppercase tracking-wider text-muted-foreground">
                Ledger (0)
              </dt>
              <dd className="m-0 font-mono font-semibold text-sm tabular-nums text-foreground">
                ${formatExactAmount('0')}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-[10px] uppercase tracking-wider text-muted-foreground">
                Bank (0)
              </dt>
              <dd className="m-0 font-mono font-semibold text-sm tabular-nums text-foreground">
                ${formatExactAmount('0')}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-[10px] uppercase tracking-wider text-muted-foreground">
                Difference
              </dt>
              {/* Not a zero, and deliberately not green. A zero in the balanced color
                  would state that the two sides agree, when what is true is that
                  nothing is selected. The arithmetic belongs to the matching story. */}
              <dd className="m-0 font-mono font-semibold text-sm tabular-nums text-muted-foreground">
                <span aria-hidden="true">&ndash;</span>
                <span className="sr-only">No rows selected</span>
              </dd>
            </div>
          </dl>
          <p className="m-0 text-[11px] text-muted-foreground">
            Selecting rows and committing a match are not part of this screen yet.
          </p>
        </section>
      </div>
    </div>
  );
};
